import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { AuctionInsightService } from '@bingads-webui-campaign/auction-insight-service';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { FilterService } from '@bingads-webui/filter-service';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { CampaignAdgroupSelectorFilterAgent } from '@bingads-webui-campaign-react/campaign-adgroup-selector-filter';

import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';

import {
  CAMPAIGN_TYPES,
  GENERAL_INSIGHTS_FEATURE_NAMES,
} from '@bingads-webui-campaign/competition-auction-insight-preferences';

import {
  getFilterConfig,
  GENERAL_INSIGHTS_DATA_TYPES,
} from '@bingads-webui-campaign-react/competition-core';

import { GeneralInsightsGrid } from './general-insights-grid';
import { initializeDataSources, getDataSource } from './general-insights-data-source';
import { GeneralInsightsChartContainer } from './general-insights-chart-container';

const appConfig = globalContext.require('component/config/index');
const permissions = appConfig.get('Permissions');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/competition-tab');
const instrumentation = globalContext.requireDefault('campaignui/feature/instrumentation/index');
const appState = globalContext.require('component/state/index');
const dateUtil = globalContext.require('component/humanize/date');
const sessionStore = globalContext.requireDefault('component/js-data-storage/session-storage');
const preferenceStoreFactory = globalContext.require('component/bingads-grid/service/filter-preferences').getFilters;
const cacheService = globalContext.require('Services/CacheService');
const dataContextManager = globalContext.require('SummaryDataContextManager');
const edmCampaign = globalContext.requireDefault('component/edm-campaign/index');
const jsdata = globalContext.require('component/js-data-campaign/index');

const isBAACompetitionBSCAuctionInsightsEnabled =
  permissions.IsBAACompetitionBSCAuctionInsightsEnabled;

// Cache the function's result.
// Solve the 'i18n not ready' issue. Memoizing the funciton is a solution
export const UsedImpressions = _.memoize(props => (
  <div className="auction-insights-used-impressions">
    <span className="bold">
      {i18n.getString('AuctionInsights_DataExplanation_Bold_Without_Percent')
        .replace('{0}', i18n.formatPercent(
          props.used,
          {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }
        ))}
    </span>
    <span>&nbsp;</span>
    <span className="usedkeywords">
      {i18n.getString('AuctionInsights_DataExplanationWithoutKeywordCount')}
    </span>
    <span>&nbsp;</span>
    <button className="HelpTopic btn-link learn-more" query="app51008">
      {i18n.getString('LearnMore')}
    </button>
  </div>
), props => props.used);

UsedImpressions.propTypes = {
  used: PropTypes.number.isRequired,
};

export const GeneralInsightsFooter = _.memoize(() => (
  <div className="summarypage-bifreshtime">
    <span>
      {i18n.getString('BIAuctionInsightStampNotes_Label')}
    </span>
    <button className="HelpTopic btn-link learn-more" query="ext54480">
      {i18n.getString('LearnMore')}
    </button>
  </div>
));


export class GeneralInsights extends React.Component {
  static propTypes = {
    customDataService: PropTypes.objectOf(PropTypes.any),
    el: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]).isRequired,
    grid: PropTypes.shape({
      addDateChangeListener: PropTypes.func.isRequired,
      showAlertHandler: PropTypes.func.isRequired,
    }).isRequired,
    scope: PropTypes.shape({
      customerId: PropTypes.string.isRequired,
      accountId: PropTypes.string,
      campaignId: PropTypes.string,
      adGroupId: PropTypes.string,
      token: PropTypes.string.isRequired,
      levelAt: PropTypes.string.isRequired,
      entityIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    campaignType: PropTypes.string.isRequired,
    onCampaignTypeSelectChange: PropTypes.func.isRequired,
    /* eslint-disable react/forbid-prop-types, react/require-default-props */
    preferenceConfig: PropTypes.object,
    parentScenario: PropTypes.object,
    perfMarker: PropTypes.shape({
      willInit: PropTypes.func.isRequired,
      willFetchData: PropTypes.func.isRequired,
      willRender: PropTypes.func.isRequired,
      done: PropTypes.func.isRequired,
      createChild: PropTypes.func.isRequired,
    }),
  };

  static defaultProps = {
    customDataService: null,
    perfMarker: {
      willInit: _.noop,
      willFetchData: _.noop,
      willRender: _.noop,
      done: _.noop,
      createChild() {
        return this;
      },
    },
  };

  constructor(props) {
    super(props);
    props.perfMarker.willInit();

    const { scope } = this.props;
    this.currentScenario = instrumentation.loadGeneralInsights.create(props.parentScenario);
    this.currentActivity = this.currentScenario.loadGeneralInsights.create();
    this.showAlertHandler = this.props.grid && this.props.grid.showAlertHandler;

    this.campaignTypes = _.map(_.values(CAMPAIGN_TYPES), (campaignType, index) => ({
      id: index,
      value: i18n.getString(`AuctionInsights_CampaignType_${campaignType}`),
      campaignType,
    }));

    this.dataService = this.props.customDataService || new AuctionInsightService({
      scope,
      showAlertHandler: this.showAlertHandler,
      i18n,
      currentScenario: this.currentScenario,
      currentActivity: this.currentActivity,
      odataPath: appConfig.get('adinsight.odata.url'),
      campaignType: this.useShoppingCampaignType() ? CAMPAIGN_TYPES.Shopping : '',
    });

    this.gridFeatureName = GENERAL_INSIGHTS_FEATURE_NAMES[props.campaignType];

    const campaignTypeForQuery = props.campaignType === CAMPAIGN_TYPES.Shopping ?
      "Enum.CampaignType'Shopping'" : "Enum.CampaignType'Default'";
    this.filterConfig = getFilterConfig({
      scope,
      dataService: this.dataService,
      campaignType: props.campaignType,
      featureName: this.gridFeatureName,
      existingCampaignFilter: this.showCampaignTypeDropdown() && props.campaignType && {
        CampaignType: {
          $eq: campaignTypeForQuery,
        },
      },
      appState,
      dateUtil,
      sessionStore,
      preferenceStoreFactory,
      cacheService,
      dataContextManager,
      edmCampaign,
      jsdata,
      i18n,
      permissions,
    });
    this.filterService = new FilterService(this.filterConfig);

    // Set up QueryBuilder based DateSource for Grid and Chart
    initializeDataSources({
      dataService: this.dataService,
      filterService: this.filterService,
      featureName: this.gridFeatureName,
    });

    this.chartDataSource = getDataSource(GENERAL_INSIGHTS_DATA_TYPES.Chart);

    this.initializeGrid();

    // When the grid reloaded from the datasource, update data state
    this.grid.on('didReload', () => {
      this.refreshData();
      if (this.mounted) {
        this.setState({
          usedImpressions: this.dataService.getUsedImpressions(),
        });
      }
    });

    props.perfMarker.willFetchData();
  }

  state = {
    chartData: null,
    showChart: true,
    usedImpressions: 0,
  };

  componentDidMount() {
    // Use this trick before we figure out the long term solution
    // https://reactjs.org/blog/2015/12/16/ismounted-antipattern.html
    this.mounted = true;
  }

  componentDidUpdate(prevProps, prevState) {
    // We only care the first render after chartData first turn non null
    if (prevState.chartData === null && this.state.chartData !== null) {
      this.props.perfMarker.done();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.upperToolBar) {
      this.upperToolBar.remove();
    }
  }

  onFilterUIChanged = (state) => {
    if (state.showToolBar) {
      this.grid.showLowerToolbar();
    } else {
      this.grid.hideLowerToolbar();
    }
  }

  onFilterStateChanged = () => {
    this.grid.refresh();
  }

  initializeGrid() {
    const ops = _.defaults({
      scope: this.props.scope,
      container: this.props.el,
      filterService: this.filterService,
      currentScenario: this.currentScenario,
      currentActivity: this.currentActivity,
      preferenceConfig: this.props.preferenceConfig,
      toggleChartToolbar: this.toggleChartToolbar,
      campaignType: this.props.campaignType,
      featureName: this.gridFeatureName,
      onCampaignTypeSelectChange: this.props.onCampaignTypeSelectChange,
      showCampaignTypeDropdown: this.showCampaignTypeDropdown(),
      showAlertHandler: this.showAlertHandler,
    }, this.props.grid);
    this.grid = new GeneralInsightsGrid(ops);
    this.upperToolBar = this.grid.getUpperToolbar();
  }

  toggleChartToolbar = () => {
    this.setState(prevState => ({
      showChart: !prevState.showChart,
    }));
  }

  refreshData() {
    this.currentActivity.trace('start refresh chart data', 'GeneralInsights/refreshData');
    const refreshChartActivity = this.currentScenario.refreshChart.create();
    refreshChartActivity.start();

    this.chartDataSource.query()
      .then((chartData) => {
        this.currentActivity.trace('end refresh chart data', 'GeneralInsights/refreshData');
        refreshChartActivity.stop();
        if (this.mounted) {
          this.setState({ chartData });
        }
      });
  }

  showCampaignTypeDropdown() {
    return isBAACompetitionBSCAuctionInsightsEnabled &&
      (this.props.scope.levelAt === levelAtConstant.ACCOUNT);
  }

  useShoppingCampaignType() {
    return isBAACompetitionBSCAuctionInsightsEnabled &&
      this.props.campaignType === CAMPAIGN_TYPES.Shopping;
  }

  render() {
    // when we get chartData, it means all data have been fetched
    if (this.state.chartData) {
      this.props.perfMarker.willRender();
    }

    return (
      <div className="general-insights">
        {this.state.usedImpressions !== 0 &&
          <UsedImpressions used={this.state.usedImpressions} />}
        <CampaignAdgroupSelectorFilterAgent
          filterConfig={this.filterConfig}
          filterService={this.filterService}
          upperToolBar={this.upperToolBar}
          onFilterUIChanged={this.onFilterUIChanged}
          onFilterStateChanged={this.onFilterStateChanged}
        />
        <div className="chart-container">
          {this.state.showChart &&
            this.state.chartData &&
            <GeneralInsightsChartContainer
              data={this.state.chartData}
              currentScenario={this.currentScenario}
              currentActivity={this.currentActivity}
              isShoppingCampaignType={this.useShoppingCampaignType()}
              parentPerfMarker={this.props.perfMarker}
            />}
        </div>

        <div className="bingadsgrid">
          <div className="grid-table" >
            <BackboneInstanceWrapper instance={this.grid} />
            <div className="grid-noresult" />
          </div>
        </div>
        <GeneralInsightsFooter />
      </div>
    );
  }
}

